import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { subscribeToNewsletter } from "../Utils/storeService";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Newsletter opt-in state and submission, shared by every place the signup form appears.
 *
 * The form used to write the address to localStorage only, which meant a subscriber existed
 * solely in the browser that typed it — nobody could ever send them anything. This posts to the
 * backend, which is what actually records consent and queues the welcome mail.
 */
export const useNewsletterSubscribe = ({ source = "web", onSuccess, onError } = {}) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const submit = useCallback(
    async (event) => {
      event?.preventDefault?.();
      const trimmed = email.trim();

      if (!trimmed || !EMAIL_PATTERN.test(trimmed)) {
        onError?.(
          t("farms.subscribe.invalid_email", {
            defaultValue: "Please enter a valid email",
          })
        );
        return;
      }

      setSubmitting(true);
      try {
        await subscribeToNewsletter({
          email: trimmed,
          language: i18n.resolvedLanguage || i18n.language || "al",
          source,
        });
        setSubscribed(true);
        setEmail("");
        onSuccess?.(
          t("farms.subscribe.success", {
            defaultValue: "You're subscribed! We'll keep you updated.",
          })
        );
      } catch (error) {
        // A 400 is the server rejecting the address; anything else is us being unreachable.
        const invalid = error?.response?.status === 400;
        onError?.(
          invalid
            ? t("farms.subscribe.invalid_email", {
                defaultValue: "Please enter a valid email",
              })
            : t("farms.subscribe.failed", {
                defaultValue:
                  "We couldn't subscribe you right now. Please try again later.",
              })
        );
      } finally {
        setSubmitting(false);
      }
    },
    [email, i18n.language, i18n.resolvedLanguage, onError, onSuccess, source, t]
  );

  return { email, setEmail, submit, submitting, subscribed };
};

export default useNewsletterSubscribe;
